import { Button } from '@askable/ui/core/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@askable/ui/core/command';
import { Popover, PopoverContent, PopoverTrigger } from '@askable/ui/core/popover';
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import countryCodeData from 'lib/data/phoneCountryCodes.json';

type Country = (typeof countryCodeData)[number];
const DEFAULT_COUNTRY: Country = countryCodeData.find(country => country.region === 'AU')!;

type Props = {
  currentRegion?: string;
  onChange: (country: Country) => void;
};
export default function CountryCodeSelect({ currentRegion, onChange }: Props) {
  const [open, setOpen] = useState(false);

  const _current = useMemo(
    () => countryCodeData.find(country => country.region === currentRegion) || DEFAULT_COUNTRY,
    [currentRegion],
  );

  const getCountryFromName = (name: string) =>
    countryCodeData.find(country => country.name.toLowerCase() === name.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Radix's PopoverTrigger merges `aria-controls` onto this child, where the rule cannot see it. */}
        {/* eslint-disable-next-line jsx-a11y/role-has-required-aria-props -- Radix PopoverTrigger adds aria-controls at runtime */}
        <Button variant="ghost" role="combobox" aria-expanded={open} className="h-full px-2 pr-1">
          <img
            id="countryFlag"
            className="h-5 w-5"
            src={`https://askable-misc-public.s3-ap-southeast-2.amazonaws.com/country_flags/${_current.region.toLowerCase()}.svg`}
            alt={`flag for ${_current?.region}`}
          />
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start" side="bottom">
        <Command>
          <CommandInput placeholder="Filter countries" borderless />
          <CommandEmpty>No country found.</CommandEmpty>
          <CommandGroup className="max-h-64 overflow-y-scroll">
            {countryCodeData.map(country => (
              <CommandItem
                className="cursor-pointer gap-2"
                key={country.name}
                value={country.name}
                onSelect={currentValue => {
                  const currentCountry = getCountryFromName(currentValue);
                  onChange(currentCountry || DEFAULT_COUNTRY);
                  setOpen(false);
                }}
              >
                <img
                  className="size-5"
                  src={`https://askable-misc-public.s3-ap-southeast-2.amazonaws.com/country_flags/${country.region.toLowerCase()}.svg`}
                  alt=""
                />
                <div>
                  {country.name} ({country.country_code})
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
